import React, { useEffect, useRef, useState } from 'react'
import play from '../images/play-buttton.png';
// Since we don't have actual volume icons, we'll use text for now
// In a real implementation, you would import volume icons:

export const VideoView = (ele) => {
    const videRef = useRef(null)
    const divRef = useRef(null)
    const [videoplay, setVideoPlay] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [volume, setVolume] = useState(1); // Volume level from 0 to 1
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);

    const repeat = 5; // 5 seconds for repeat

    // Safely play video with error handling
    const safePlay = (videoElement) => {
        if (!videoElement) return;

        // Use promise-based play with catch to handle errors
        const playPromise = videoElement.play();

        // Only handle if play() returns a promise (modern browsers)
        if (playPromise !== undefined) {
            playPromise.catch(error => {
                // Ignore the power-saving interruption errors
                if (error.name !== 'NotAllowedError') {
                    console.error('Video play error:', error);
                }
                // Don't keep trying to play in background if browser is preventing it
            });
        }
    };

    // Handle video ended event
    const handleVideoEnded = () => {
        if (!videRef.current) return;

        if (!videoplay) {
            // If videoplay is false, repeat the video immediately
            videRef.current.currentTime = 0;
            // Only try to play if the document is visible
            if (!document.hidden) {
                safePlay(videRef.current);
            }
        } else if (ele?.isActive) {
            // If video is active and videoplay is true, reset to initial state with play button
            setVideoPlay(false); // This will show the play button again
            setIsMuted(true);   // Mute the video
            videRef.current.currentTime = 0; // Reset to beginning
        }
    };

    // Handle video pause event
    const handleVideoPause = () => {
        if (ele?.isActive) {
            setIsPaused(true);

            // When paused, mute the video
            videRef.current.muted = true;
            setIsMuted(true);
        }
    };

    // Add a time update listener to enforce the 5-second limit only when not in normal playback
    const handleTimeUpdate = () => {
        // Only enforce the 5-second limit when:
        // 1. Video is paused (isPaused is true) OR
        // 2. Video is not in active/play state (not (isActive && videoplay))
        if (videRef.current && videRef.current.currentTime >= repeat) {
            // If in normal playback mode (active and playing), don't reset
            if (ele?.isActive && videoplay === true && !isPaused) {
                // Let it play through naturally
                return;
            }

            // Otherwise, reset and play again
            videRef.current.currentTime = 0;
            if (!document.hidden) {
                safePlay(videRef.current);
            }
        }
    };

    // Set up an interval to check and repeat video if it's paused
    useEffect(() => {
        // Even when paused, we want to restart after 5 seconds
        let pauseInterval;

        if (isPaused && ele?.isActive) {
            pauseInterval = setInterval(() => {
                if (isPaused && videRef.current && !document.hidden) {
                    videRef.current.currentTime = 0;
                    safePlay(videRef.current);
                }
            }, repeat * 1000);
        }

        return () => {
            if (pauseInterval) {
                clearInterval(pauseInterval);
            }
        };
    }, [isPaused, ele?.isActive]);

    // Handle video play event
    const handleVideoPlay = () => {
        // Just update the state to indicate it's not paused anymore
        // No need to reset the time - continue from where it was paused
        setIsPaused(false);
    };

    // Handle toggling mute state only (since we removed the volume slider)
    const handleVolumeToggle = () => {
        if (!videRef.current) return;

        const newMutedState = !isMuted;
        setIsMuted(newMutedState);

        if (videRef.current) {
            videRef.current.muted = newMutedState;

            // Keep track of last unmuted volume level
            if (!newMutedState) {
                // When unmuting, restore to previous volume or default to 1
                videRef.current.volume = volume || 1;
            }

            // If video has somehow paused, restart it
            // Use requestAnimationFrame to ensure UI updates first
            requestAnimationFrame(() => {
                if (videRef.current && videRef.current.paused && ele?.isActive && videoplay && !document.hidden) {
                    safePlay(videRef.current);
                }
            });
        }
    };

    useEffect(() => {
        if (!videRef.current) return;

        // Add event listeners
        videRef.current.addEventListener('ended', handleVideoEnded);
        videRef.current.addEventListener('pause', handleVideoPause);
        videRef.current.addEventListener('play', handleVideoPlay);
        videRef.current.addEventListener('timeupdate', handleTimeUpdate);

        // Initial setup based on state
        if (ele?.isActive && videoplay === true) {
            videRef.current.currentTime = 0;
            videRef.current.muted = false;
            setIsMuted(false);
            videRef.current.volume = volume;
            videRef.current.controls = false; // Always hide native controls
            if (!document.hidden) {
                safePlay(videRef.current);
            }
        } else {
            videRef.current.muted = true;
            setIsMuted(true);
            videRef.current.controls = false;
            videRef.current.currentTime = 0;
            if (!document.hidden) {
                safePlay(videRef.current);
            }
        }

        // Cleanup event listeners
        return () => {
            if (videRef.current) {
                videRef.current.removeEventListener('ended', handleVideoEnded);
                videRef.current.removeEventListener('pause', handleVideoPause);
                videRef.current.removeEventListener('play', handleVideoPlay);
                videRef.current.removeEventListener('timeupdate', handleTimeUpdate);
            }
        };
    }, [ele?.isActive, videoplay]);

    return (
        <div className="slide-content">
            <div className="video"
                ref={divRef}
                onClick={() => {
                    if (videoplay === true) {
                        setVideoPlay(false);
                        videRef.current.muted = false;
                    }
                }}
            >
                {ele.isActive && (videoplay === true)
                    ?
                    <></>
                    :
                    <div className='play-now'>
                        <div className="play">
                            <img src={play} alt="play"
                                onClick={() => {
                                    setVideoPlay(true);
                                }}
                            />
                        </div>
                        {/* <img src={ele.image} alt={ele.left.data} className='w-100' /> */}
                    </div>
                }
                <div className="video-container position-relative">
                    <video
                        src={ele.video}
                        className='w-100'
                        autoPlay={true}
                        playsInline
                        webkit-playsinline={true}
                        controls={false}
                        webkit-controls={false}
                        muted={isMuted}
                        ref={videRef}
                        style={{ display: "block", objectFit: "cover" }}
                    ></video>

                    {/* Volume controls - only shown when video is playing (not paused and videoplay is true) */}
                    {!isPaused && ele?.isActive && videoplay === true && (
                        <div
                            className="volume-controls"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{
                                position: 'absolute',
                                bottom: '30px',
                                right: '30px',
                                zIndex: 10,
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {/* Volume button - simplified */}
                            <button
                                className="volume-control-btn"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleVolumeToggle();
                                }}

                                style={{
                                    cursor: 'pointer',
                                    outline: "none",
                                    border: "none",
                                    background: "transparent",
                                    fontSize: "24px",
                                    color: "#18E291",
                                    textShadow: "0px 0px 10px rgba(255,255,255,0.5)"
                                }}
                            >
                                {isMuted ? <i className="fa-solid fa-volume-mute"></i> : <i className="fa-solid fa-volume-high"></i>}
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <div className="stats-content">
                <div className="left">
                    <span>{ele.left.numbers}</span>
                    <p>{ele.left.data}</p>
                </div>
                <div className="right">
                    <span>{ele.right.numbers}</span>
                    <p>{ele.right.data}</p>
                </div>
            </div>
        </div>
    )
}
